import React, { FC, memo, useCallback } from 'react';
import styled from '@emotion/styled';
import { Textfit } from 'react-textfit';
import { useButton, useCurrency } from '~Hooks';
import { FormattedModificationGroup } from '~Hooks/ItemModifications/useItemModifications';
import { MODIFICATION_GROUP_TYPE } from '~../__queries__/globalTypes';
import ItemImage from '../../../Components/ItemImage';
import RadioButton from '../../../Components/RadioButton';
import CheckBox from '../../../Components/CheckBox';

/** Small thumbnail on the left when the variation has a photo. */
const VARIATION_IMAGE_WIDTH = 60;

/**
 * One selectable variation: photo (when it has one) and name on the left,
 * then the price and the indicator on the right - a radio in a choose-one
 * group, a checkbox in a choose-many group. Selected means the indicator
 * fills and the frame takes the button color - the text stays put, so a row
 * of options reads like a form, not like buttons. Selection is a class, not a
 * styled prop, so only this node changes.
 */
const Option = styled.button`
  appearance: none;
  border: 2px solid var(--eposBorderColor);
  border-radius: 15px;
  padding: 12px 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Breathing room between the name and the price. */
  gap: 18px;
  cursor: pointer;
  text-align: left;
  font-family: var(--eposFontFamily);
  font-size: 14px;
  font-weight: 600;
  background: transparent;
  color: var(--eposText);
  transition: transform 0.15s ease-out, border-color 0.15s ease-out;

  &.is-touching {
    transform: scale(0.97);
  }

  &.is-selected {
    border-color: var(--eposButton);
  }

  &.is-out-of-stock {
    opacity: 0.4;
    cursor: default;
  }
`;

const OptionPrice = styled.span`
  white-space: nowrap;
  font-weight: 800;
  opacity: 0.7;

  .is-selected & {
    opacity: 1;
  }
`;

/** Image + name, taking all the width the trail leaves so the name has a box to fit into. */
const OptionLead = styled.span`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

/** Price + indicator, never wrapping. */
const OptionTrail = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
`;

/** One line, shrinking the type until the name fits beside the price - a fixed height so Textfit has a box to measure. */
const OptionName = styled.span`
  flex: 1;
  min-width: 0;
  height: 18px;
  line-height: 18px;
`;

type Variation = FormattedModificationGroup['variations'][number];

interface Props {
  variation: Variation;
  /** SINGLE draws a radio, MULTIPLE a checkbox - the shape says how many may be picked. */
  groupType: MODIFICATION_GROUP_TYPE;
  onSelect: (id: string) => void;
}

/**
 * useItemModifications rebuilds every variation object on each selection, so a
 * reference check would re-render the whole group per tap. Compare the fields
 * that are drawn instead - only the option whose `selected` flipped renders.
 */
const propsAreEqual = (prev: Props, next: Props) =>
  prev.onSelect === next.onSelect &&
  prev.groupType === next.groupType &&
  prev.variation.id === next.variation.id &&
  prev.variation.selected === next.variation.selected &&
  prev.variation.isOutOfStock === next.variation.isOutOfStock &&
  prev.variation.name === next.variation.name &&
  prev.variation.price === next.variation.price &&
  prev.variation.imageUrl === next.variation.imageUrl;

const VariationOption: FC<Props> = memo(({ variation, groupType, onSelect }) => {
  const currency = useCurrency();
  const handleSelect = useCallback(() => onSelect(variation.id), [onSelect, variation.id]);
  const { touching, disabled, disabledToast, ...button } = useButton({
    onClick: handleSelect,
    disabled: variation.isOutOfStock,
  });

  const className = [
    touching && 'is-touching',
    variation.selected && 'is-selected',
    variation.isOutOfStock && 'is-out-of-stock',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Option
      {...button}
      className={className || undefined}
      data-testid="epos-variation"
      data-variation-id={variation.id}
      aria-pressed={variation.selected}>
      <OptionLead>
        {!!variation.imageUrl && (
          <ItemImage
            imageUrl={variation.imageUrl}
            cdnWidth={VARIATION_IMAGE_WIDTH * 2}
            width={`${VARIATION_IMAGE_WIDTH}px`}
            borderRadius={10}
          />
        )}
        <OptionName>
          <Textfit mode="single" max={14} min={9}>
            {variation.name}
          </Textfit>
        </OptionName>
      </OptionLead>
      <OptionTrail>
        {variation.price > 0 && <OptionPrice>+{currency.format(variation.price)}</OptionPrice>}
        {groupType === MODIFICATION_GROUP_TYPE.SINGLE ? (
          <RadioButton checked={variation.selected} />
        ) : (
          <CheckBox checked={variation.selected} />
        )}
      </OptionTrail>
    </Option>
  );
}, propsAreEqual);

VariationOption.displayName = 'VariationOption';

export default VariationOption;
